import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2, ShieldX, X } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { toast } from "sonner";

/** Confirmation modal for deleting a user account or a restaurant from the admin console.
 * Two modes: soft (deactivate + anonymise, keeps accounting history — recommended)
 * or hard (irreversible wipe, requires an explicit checkbox). */
export default function DeleteAccountModal({ target, onClose, onDeleted }) {
  const { t } = useTranslation();
  const [impact, setImpact] = useState(null);
  const [confirmHard, setConfirmHard] = useState(false);
  const [busy, setBusy] = useState(false);

  const base = target.kind === "restaurant" ? `/admin/restaurants/${target.id}` : `/admin/users/${target.id}`;

  useEffect(() => {
    apiClient.get(`${base}/delete-impact`).then(({ data }) => setImpact(data)).catch(() => setImpact({}));
  }, [base]);

  const doDelete = async (hard) => {
    setBusy(true);
    try {
      await apiClient.delete(hard ? `${base}?hard=true` : base);
      toast.success(hard ? t("admin.hardDeleteDone") : t("admin.softDeleteDone"));
      onDeleted();
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const net = impact?.pending_net || 0;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" data-testid="delete-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-ink" data-testid="delete-modal-close" aria-label="Fermer">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-brand-red/10 text-brand-red grid place-items-center shrink-0">
            <Trash2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">Supprimer ce compte ?</h3>
            <div className="text-sm text-mute">{target.name} · <span className="uppercase text-xs tracking-widest">{target.role}</span></div>
          </div>
        </div>

        {!impact ? (
          <div className="py-6 text-center text-mute"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-2 mb-4">
            {impact.has_orders && (
              <div className="flex items-start gap-2 text-sm bg-brand-yellow/15 rounded-xl p-3" data-testid="delete-warn-orders">
                <AlertTriangle className="w-4 h-4 text-[#946A00] shrink-0 mt-0.5" />
                <span>Ce compte a un <b>historique de commandes</b>. La désactivation est recommandée pour préserver la comptabilité.</span>
              </div>
            )}
            {net < 0 && (
              <div className="flex items-start gap-2 text-sm bg-brand-red/10 rounded-xl p-3" data-testid="delete-warn-debt">
                <AlertTriangle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
                <span><b>Dette cash en cours : {formatGNF(-net)}</b> dus à NelDaari. Régularisez avant de supprimer définitivement.</span>
              </div>
            )}
            {net > 0 && (
              <div className="flex items-start gap-2 text-sm bg-brand-red/10 rounded-xl p-3" data-testid="delete-warn-payout">
                <AlertTriangle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
                <span><b>{formatGNF(net)} de reversements en attente</b> pour ce compte. Versez-les avant une suppression définitive.</span>
              </div>
            )}
            {!impact.has_orders && net === 0 && (
              <div className="text-sm text-mute">Aucun historique de commandes ni solde en attente.</div>
            )}
          </div>
        )}

        <div className="space-y-2">
          <button
            onClick={() => doDelete(false)}
            disabled={busy || !impact}
            data-testid="delete-soft-btn"
            className="w-full rounded-xl bg-ink hover:bg-black text-white py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <ShieldX className="w-4 h-4" /> Désactiver le compte (recommandé)
          </button>
          <label className="flex items-center gap-2 text-xs text-mute px-1 pt-1 cursor-pointer">
            <input type="checkbox" checked={confirmHard} onChange={(e) => setConfirmHard(e.target.checked)} data-testid="delete-hard-confirm" />
            Je comprends que la suppression définitive est <b>irréversible</b>
          </label>
          <button
            onClick={() => doDelete(true)}
            disabled={busy || !impact || !confirmHard}
            data-testid="delete-hard-btn"
            className="w-full rounded-xl bg-brand-red hover:bg-[#C21E2C] text-white py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" /> Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  );
}
